import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore, cleanupThemeStore } from '@/stores/themeStore';
import { cleanupPreferencesStore } from '@/stores/preferencesStore';
import { cleanupRealtimeStore } from '@/stores/realtimeStore';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { SetupCheck } from '@/components/SetupCheck';
import { MainLayout } from '@/layouts/MainLayout';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { OidcSessionSync } from '@/components/OidcSessionSync';
import AccessibilitySettings from '@/components/AccessibilitySettings';
import KeyboardShortcuts from '@/components/KeyboardShortcuts';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AsyncErrorBoundary, CriticalErrorBoundary } from '@/components/errors';
import { ScreenReaderAnnouncer } from '@/components/accessibility/ScreenReaderAnnouncer';
import GlobalErrorHandler from '@/components/GlobalErrorHandler';
import ToastContainer from '@/components/Toast';
import RTLWrapper from '@/components/RTLWrapper';
import { syncPendingMutations } from '@/utils/offlineStorage';
import { registerPreload, preloadLikelyTargets } from '@/utils/routePreloader';
import { PageLoader } from '@/components/LazyLoadFallbacks';
import { initErrorReporting } from '@/services/errorReporting';
import { PerformanceProvider } from '@/components/PerformanceProvider';
import CookieConsent from '@/components/CookieConsent';
import axios from 'axios';
import { logger } from '@/utils/logger';
import { safeLazy } from '@/utils/safeLazy';
import { useOidcAuth } from '@/hooks/useOidcAuth';
import { isOidcEnabled } from '@/auth/oidcClient';

// Initialize error reporting service on app load
// Using process.env for Node.js compatibility with Vite's define
const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
const isPerformanceEnabled = isDev || import.meta.env.VITE_PERF_ENABLED === 'true';
initErrorReporting({
  environment: isDev ? 'development' : 'production',
  sendToBackend: !isDev,
  logToConsole: true,
});

// =============================================================================
// LAZY-LOADED PAGES WITH CODE SPLITTING
// =============================================================================
// Each page is loaded as a separate chunk for optimal initial bundle size.
// Preload functions are registered for intelligent prefetching.

// Core Authentication & Setup Pages
// Using safeLazy for critical routes to handle network failures with retry logic
const Login = safeLazy(() => import('@/pages/Login'), { displayName: 'Login' }, 'Login');
const OidcCallback = safeLazy(() => import('@/pages/OidcCallback'), { displayName: 'OidcCallback' }, 'OidcCallback');
const OidcSilentCallback = safeLazy(() => import('@/pages/OidcSilentCallback'), { displayName: 'OidcSilentCallback' }, 'OidcSilentCallback');
const SetupWizard = safeLazy(() => import('@/pages/SetupWizard'), { displayName: 'SetupWizard' }, 'SetupWizard');
const WelcomeDashboard = safeLazy(() => import('@/pages/WelcomeDashboard'), { displayName: 'WelcomeDashboard' }, 'WelcomeDashboard');

// Interview Flow Pages - critical paths with retry support
const InterviewStart = safeLazy(() => import('@/pages/InterviewStart'), { displayName: 'InterviewStart' }, 'InterviewStart');
const IdentityCapture = safeLazy(() => import('@/pages/IdentityCapture'), { displayName: 'IdentityCapture' }, 'IdentityCapture');
const TakeInterview = safeLazy(() => import('@/pages/TakeInterview'), { displayName: 'TakeInterview' }, 'TakeInterview');
const LiveInterview = safeLazy(() => import('@/pages/LiveInterview'), { displayName: 'LiveInterview' }, 'LiveInterview');
const PublicInterview = safeLazy(() => import('@/pages/PublicInterview'), { displayName: 'PublicInterview' }, 'PublicInterview');

// Main Dashboard & Analytics
const Dashboard = safeLazy(() => import('@/pages/Dashboard'), { displayName: 'Dashboard' }, 'Dashboard');
const Analytics = lazy(() => import('@/pages/Analytics').then(m => ({ default: m.Analytics })));
const JourneyAnalytics = lazy(() => import('@/pages/JourneyAnalytics'));
const HiringAnalytics = lazy(() => import('@/pages/HiringAnalytics').then(m => ({ default: m.HiringAnalytics })));
const CalibrationPage = lazy(() => import('@/pages/analytics/CalibrationPage').then(m => ({ default: m.CalibrationPage })));

// Associate Management
const Associates = lazy(() => import('@/pages/Associates').then(m => ({ default: m.Associates })));
const AssociateProfile = lazy(() => import('@/pages/AssociateProfile').then(m => ({ default: m.AssociateProfile })));
const InviteAssociates = lazy(() => import('@/pages/InviteAssociates').then(m => ({ default: m.InviteAssociates })));

// Interview Management
const Interviews = lazy(() => import('@/pages/Interviews').then(m => ({ default: m.Interviews })));
const ReviewQueue = lazy(() => import('@/pages/ReviewQueue').then(m => ({ default: m.ReviewQueue })));
const ReviewInterview = lazy(() => import('@/pages/ReviewInterview').then(m => ({ default: m.ReviewInterview })));
const ReviewPlayback = lazy(() => import('@/pages/ReviewPlayback').then(m => ({ default: m.ReviewPlayback })));
const InterviewComparison = lazy(() => import('@/pages/InterviewComparison').then(m => ({ default: m.InterviewComparison })));
const InterviewCoach = lazy(() => import('@/pages/InterviewCoach').then(m => ({ default: m.InterviewCoach })));

// Questions & Templates
const Questions = lazy(() => import('@/pages/Questions').then(m => ({ default: m.Questions })));
const Templates = lazy(() => import('@/pages/Templates').then(m => ({ default: m.Templates })));
const TemplateDetail = lazy(() => import('@/pages/TemplateDetail').then(m => ({ default: m.TemplateDetail })));
const TemplateEditor = lazy(() => import('@/pages/TemplateEditor').then(m => ({ default: m.TemplateEditor })));
const IQQuestions = lazy(() => import('@/pages/IQQuestions'));
const RubricBuilder = lazy(() => import('@/pages/RubricBuilder'));

// Reports & Settings
const Reports = lazy(() => import('@/pages/Reports').then(m => ({ default: m.Reports })));
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));
const AuditLogs = lazy(() => import('@/pages/AuditLogs').then(m => ({ default: m.AuditLogs })));
const Preferences = lazy(() => import('@/pages/Preferences'));
const Backups = lazy(() => import('@/pages/Backups').then(m => ({ default: m.Backups })));

// Admin Pages
const APIKeys = lazy(() => import('@/pages/APIKeys'));
const Developers = lazy(() => import('@/pages/Developers'));
const TwoFactorSetup = lazy(() => import('@/pages/TwoFactorSetup').then(m => ({ default: m.TwoFactorSetup })));
const RoleManagement = lazy(() => import('@/pages/RoleManagement'));
const TeamManagement = lazy(() => import('@/pages/TeamManagement'));

// Compliance & GDPR
const ComplianceDashboard = lazy(() => import('@/pages/ComplianceDashboard').then(m => ({ default: m.ComplianceDashboard })));
const GDPRRequests = lazy(() => import('@/pages/GDPRRequests'));
const EEOReport = lazy(() => import('@/pages/EEOReport').then(m => ({ default: m.EEOReport })));

// Talent & Hiring
const TalentPools = lazy(() => import('@/pages/TalentPools'));
const BackgroundCheckAdmin = lazy(() => import('@/pages/BackgroundCheckAdmin').then(m => ({ default: m.BackgroundCheckAdmin })));

// Branding & SSO
const BrandingSettings = lazy(() => import('@/pages/BrandingSettings'));
const SSOSettings = lazy(() => import('@/pages/SSOSettings'));

// Widget & Scheduling
const WidgetManager = lazy(() => import('@/pages/WidgetManager').then(m => ({ default: m.WidgetManager })));
const PublicBookingPage = lazy(() => import('@/pages/PublicBookingPage').then(m => ({ default: m.PublicBookingPage })));

// Error Pages
const AccessDenied = lazy(() => import('@/pages/AccessDenied'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Legal Pages (Public)
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));

// =============================================================================
// REGISTER PRELOAD FUNCTIONS
// =============================================================================
// These allow routes to be preloaded before navigation for instant page loads

registerPreload('/login', () => import('@/pages/Login'));
registerPreload('/auth/callback', () => import('@/pages/OidcCallback'));
registerPreload('/auth/silent-callback', () => import('@/pages/OidcSilentCallback'));
registerPreload('/dashboard', () => import('@/pages/Dashboard'));
registerPreload('/welcome', () => import('@/pages/WelcomeDashboard'));
registerPreload('/analytics', () => import('@/pages/Analytics'));
registerPreload('/analytics/journey', () => import('@/pages/JourneyAnalytics'));
registerPreload('/analytics/hiring', () => import('@/pages/HiringAnalytics'));
registerPreload('/analytics/calibration', () => import('@/pages/analytics/CalibrationPage'));
registerPreload('/associates', () => import('@/pages/Associates'));
registerPreload('/interviews', () => import('@/pages/Interviews'));
registerPreload('/review-queue', () => import('@/pages/ReviewQueue'));
registerPreload('/questions', () => import('@/pages/Questions'));
registerPreload('/templates', () => import('@/pages/Templates'));
registerPreload('/reports', () => import('@/pages/Reports'));
registerPreload('/settings', () => import('@/pages/Settings'));
registerPreload('/audit-logs', () => import('@/pages/AuditLogs'));
registerPreload('/preferences', () => import('@/pages/Preferences'));
registerPreload('/backups', () => import('@/pages/Backups'));
registerPreload('/compliance', () => import('@/pages/ComplianceDashboard'));
registerPreload('/talent-pools', () => import('@/pages/TalentPools'));

/**
 * Route preloader component - preloads likely navigation targets
 */
const RoutePreloader: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Preload likely targets after initial render
    const timer = setTimeout(() => {
      preloadLikelyTargets(location.pathname);
    }, 1000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return null;
};


const App: React.FC = () => {
  const { isAuthenticated: storeAuthenticated } = useAuthStore();
  const oidc = useOidcAuth();
  const oidcEnabled = isOidcEnabled();
  const isAuthenticated = oidcEnabled ? oidc.isAuthenticated : storeAuthenticated;
  // Theme is now managed entirely by themeStore - it handles:
  // - Initial theme application (with flash prevention)
  // - localStorage persistence
  // - System preference syncing
  // - Smooth transitions between themes
  const { resolvedTheme: _resolvedTheme } = useThemeStore();

  // Cleanup store event listeners on unmount only
  useEffect(() => {
    return () => {
      cleanupThemeStore();
      cleanupPreferencesStore();
      cleanupRealtimeStore();
    };
  }, []);

  // Handle retry of failed requests when back online
  const handleRetryFailedRequests = async () => {
    try {
      await syncPendingMutations(axios);
    } catch (error) {
      logger.error('Error syncing mutations:', error);
    }
  };

  return (
    <ErrorBoundary>
      <RTLWrapper>
        <ScreenReaderAnnouncer>
        <BrowserRouter>
          {/* Performance Monitoring Provider */}
          <PerformanceProvider
            enabled={isPerformanceEnabled}
            showBudgetWarnings={isDev}
            onBudgetViolation={isDev ? (violation) => {
              logger.warn('[Performance] Budget violation detected:', violation);
            } : undefined}
          >
          <SetupCheck>
            <OidcSessionSync />
            {/* Global Error Handler */}
            <GlobalErrorHandler />

            {/* Toast Notifications */}
            <ToastContainer />

            {/* Cookie Consent Banner (GDPR/ePrivacy compliance) */}
            <CookieConsent
              privacyPolicyUrl="/privacy-policy"
              cookiePolicyUrl="/privacy-policy#cookies"
            />

            {/* PWA Components */}
            <OfflineIndicator onRetry={handleRetryFailedRequests} />

            {/* Accessibility Components */}
            <AccessibilitySettings />
            <KeyboardShortcuts />

            {/* Route Preloader for intelligent prefetching */}
            <RoutePreloader />

          {/* AsyncErrorBoundary handles chunk loading failures with automatic retry */}
          <AsyncErrorBoundary resourceName="page">
            <Suspense fallback={<PageLoader />}>
              <Routes>
        {/* Public routes */}
        <Route path="/setup" element={
          <ErrorBoundary>
            <SetupWizard />
          </ErrorBoundary>
        } />

        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : (
              <ErrorBoundary>
                <Login />
              </ErrorBoundary>
            )
          }
        />

        <Route
          path="/auth/callback"
          element={
            <ErrorBoundary>
              <OidcCallback />
            </ErrorBoundary>
          }
        />

        <Route
          path="/auth/silent-callback"
          element={
            <ErrorBoundary>
              <OidcSilentCallback />
            </ErrorBoundary>
          }
        />

        <Route path="/access-denied" element={
          <ErrorBoundary>
            <AccessDenied />
          </ErrorBoundary>
        } />

        {/* Legal Pages - Public Access */}
        <Route path="/privacy-policy" element={
          <ErrorBoundary>
            <PrivacyPolicy />
          </ErrorBoundary>
        } />

        <Route path="/terms-of-service" element={
          <ErrorBoundary>
            <TermsOfService />
          </ErrorBoundary>
        } />

        {/*
         * =============================================================================
         * INTERVIEW ROUTES - DUAL ACCESS MODEL (Security Documentation)
         * =============================================================================
         *
         * These routes intentionally DO NOT use ProtectedRoute because they support
         * TWO distinct access methods:
         *
         * 1. MAGIC LINK ACCESS (External Candidates):
         *    - Candidates receive an email/SMS with a magic link containing a token
         *    - They access /interview/start?token=xyz which validates the token
         *    - A candidate session is established via sessionStorage:
         *      - candidate_interview_id: The interview UUID
         *      - candidate_interview_token: The invitation token for API calls
         *      - candidate_interview: Full interview data (JSON)
         *    - No authentication required; the token IS the authorization
         *
         * 2. AUTHENTICATED USER ACCESS (Internal Staff):
         *    - Authenticated users (admin/manager/reviewer) can access interviews
         *    - They use the standard auth token for API authorization
         *    - Useful for testing interviews or proctoring sessions
         *
         * SECURITY MODEL:
         * - Frontend: Routes are accessible to anyone, but the pages check for either:
         *   a) Valid candidate session tokens in sessionStorage, OR
         *   b) Authenticated user session in authStore
         * - Backend: API endpoints validate access via either:
         *   a) Valid invitation_token for candidate operations, OR
         *   b) Valid JWT for authenticated user operations
         * - The API is the ultimate authority for access control
         *
         * WHY NOT USE A CUSTOM GUARD?
         * - A guard would add complexity without improving security
         * - The API already enforces all access rules
         * - The current model allows seamless transitions (e.g., candidate can
         *   complete interview on different device by rescanning QR)
         *
         * RELATED ROUTES:
         * - /interview/start: Entry point for magic link flow (validates token)
         * - /interview/:id: Main interview taking interface (dual access)
         * - /interview/:id/identity: Mobile ID capture via QR (requires token)
         * =============================================================================
         */}

        {/* Magic link interview entry point - validates token and establishes candidate session */}
        <Route
          path="/interview/start"
          element={
            <CriticalErrorBoundary
              sectionName="Interview Start"
              onCriticalError={(error) => {
                logger.error('Critical interview start error:', error);
              }}
            >
              <InterviewStart />
            </CriticalErrorBoundary>
          }
        />

        {/*
         * Public interview entry point - accessed via public template links.
         * No authentication required - candidates enter their name and email.
         * Creates a new interview and redirects to /interview/start with token.
         */}
        <Route
          path="/interview/public/:slug"
          element={
            <CriticalErrorBoundary
              sectionName="Public Interview"
              onCriticalError={(error) => {
                logger.error('Critical public interview error:', error);
              }}
            >
              <PublicInterview />
            </CriticalErrorBoundary>
          }
        />

        {/*
         * Mobile identity capture - accessed via QR code during interview.
         * Requires: token query param OR candidate session in sessionStorage.
         * Used when candidate needs to verify identity from their mobile phone
         * while taking the interview on a desktop computer.
         */}
        <Route
          path="/interview/:id/identity"
          element={
            <ErrorBoundary>
              <IdentityCapture />
            </ErrorBoundary>
          }
        />

        {/* Root redirect */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Protected routes with layout */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <WelcomeDashboard />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ErrorBoundary>
                  <Dashboard />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Analytics />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics/journey"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <JourneyAnalytics />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics/calibration"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Suspense fallback={<div />}>
                    <CalibrationPage />
                  </Suspense>
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/associates"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Associates />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/associates/:id"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <AssociateProfile />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/interviews"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <MainLayout>
                <ErrorBoundary>
                  <Interviews />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/interviews/:id"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <MainLayout>
                <ErrorBoundary>
                  <ReviewInterview />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/*
         * Main interview taking interface - DUAL ACCESS (see security docs above).
         * Access methods:
         * - Candidates: Via magic link flow (/interview/start) with session token
         * - Staff: Via direct navigation with authenticated session
         * The TakeInterview component checks for either access method and the API
         * validates all operations server-side.
         *
         * Uses CriticalErrorBoundary because interview taking is a critical user flow
         * that should never fail silently.
         */}
        <Route
          path="/interview/:id"
          element={
            <CriticalErrorBoundary
              sectionName="Interview"
              onCriticalError={(error) => {
                // Log critical interview errors for immediate attention
                logger.error('Critical interview error:', error);
              }}
            >
              <TakeInterview />
            </CriticalErrorBoundary>
          }
        />

        <Route
          path="/review-queue"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <MainLayout>
                <ErrorBoundary>
                  <ReviewQueue />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/review/:id"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <MainLayout>
                <ErrorBoundary>
                  <ReviewInterview />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/review/:id/playback"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <MainLayout>
                <ErrorBoundary>
                  <ReviewPlayback />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/questions"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Questions />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/templates"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Templates />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/iq-questions"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <IQQuestions />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/templates/new"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <TemplateEditor />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/templates/:id"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <TemplateDetail />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/templates/:id/edit"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <TemplateEditor />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Reports />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <Settings />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/audit-logs"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <AuditLogs />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Preferences */}
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ErrorBoundary>
                  <Preferences />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Backups - Admin Only */}
        <Route
          path="/backups"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <Backups />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* API Keys - Admin Only */}
        <Route
          path="/api-keys"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <APIKeys />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Developer Portal */}
        <Route
          path="/developers"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <Developers />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Two-Factor Setup */}
        <Route
          path="/2fa-setup"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ErrorBoundary>
                  <TwoFactorSetup />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Role Management */}
        <Route
          path="/roles"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <RoleManagement />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Team Management */}
        <Route
          path="/team"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <TeamManagement />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Hiring Analytics */}
        <Route
          path="/analytics/hiring"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <HiringAnalytics />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Interview Comparison */}
        <Route
          path="/compare"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <MainLayout>
                <ErrorBoundary>
                  <InterviewComparison />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Interview Coach */}
        <Route
          path="/coach"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <InterviewCoach />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Rubric Builder */}
        <Route
          path="/rubrics"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <RubricBuilder />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Invite Associates */}
        <Route
          path="/invite"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <InviteAssociates />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Compliance Dashboard */}
        <Route
          path="/compliance"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <ComplianceDashboard />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* GDPR Requests */}
        <Route
          path="/compliance/gdpr-requests"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <GDPRRequests />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* EEO Report */}
        <Route
          path="/compliance/eeo-report"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <EEOReport />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Talent Pools */}
        <Route
          path="/talent-pools/*"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <MainLayout>
                <ErrorBoundary>
                  <TalentPools />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Background Check Admin */}
        <Route
          path="/background-checks"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <BackgroundCheckAdmin />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Branding Settings */}
        <Route
          path="/branding"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <BrandingSettings />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* SSO Settings */}
        <Route
          path="/sso"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <SSOSettings />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Widget Manager */}
        <Route
          path="/widgets"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <MainLayout>
                <ErrorBoundary>
                  <WidgetManager />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Public Booking Page (no auth required) */}
        <Route
          path="/book/:linkId"
          element={
            <ErrorBoundary>
              <PublicBookingPage />
            </ErrorBoundary>
          }
        />

        {/* Live Interview Room - Critical section with enhanced error handling */}
        <Route
          path="/live/:roomId"
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'reviewer']}>
              <CriticalErrorBoundary
                sectionName="Live Interview"
                onCriticalError={(error) => {
                  logger.error('Critical live interview error:', error);
                }}
              >
                <LiveInterview />
              </CriticalErrorBoundary>
            </ProtectedRoute>
          }
        />

              {/* 404 Not Found */}
              <Route path="*" element={
                <ErrorBoundary>
                  <NotFoundPage />
                </ErrorBoundary>
              } />
              </Routes>
            </Suspense>
          </AsyncErrorBoundary>
          </SetupCheck>
          </PerformanceProvider>
        </BrowserRouter>
        </ScreenReaderAnnouncer>
      </RTLWrapper>
    </ErrorBoundary>
  );
};

export default App;
